export const ROUTES = {
  DASHBOARD: '/dashboard',
  CHAPEA: '/chapea',
  ARGOS: '/argos-info',
  FIELD_TEST: '/field-test-info',
  HMD: '/hmd',
  CSRM: '/csrm',
  CSRM_CHARTS: '/csrm/charts',
  HARDWARE: '/hardware',
  CONVERT_FILES: '/convert-files',
  IOT_DOWNLOAD: '/iot-download',
  CALENDAR: '/calendar',
  QUERY: '/query',
  DEV_DOWNLOAD: '/dev-download',
  UPLOAD: '/upload',
  UPDATE: '/update',
};

const ALL_ACCESS = '*';

export const ROLE_ACCESS = {
  FOD: [ROUTES.DASHBOARD, ROUTES.CALENDAR],
  ADMIN: [ALL_ACCESS],
  CHAPEA: [ROUTES.CONVERT_FILES, ROUTES.IOT_DOWNLOAD],
};

const normalizeRoles = (roles) => roles
  .map((role) => String(role || '').trim().toUpperCase())
  .filter((role) => role.length > 0);

const getMappedRoleRoutes = (roles) => roles
  .filter((role) => ROLE_ACCESS[role])
  .map((role) => ROLE_ACCESS[role]);

export const getAllowedRoutes = (roles) => {
  if (!Array.isArray(roles) || roles.length === 0) {
    return [ROUTES.DASHBOARD];
  }
  const mappedRoleRoutes = getMappedRoleRoutes(normalizeRoles(roles));
  if (mappedRoleRoutes.length === 0) {
    return [ROUTES.DASHBOARD];
  }
  if (mappedRoleRoutes.some((routes) => routes.includes(ALL_ACCESS))) {
    return [ALL_ACCESS];
  }
  const allowed = new Set(mappedRoleRoutes.flat());
  return Array.from(allowed);
};

export const canAccessRoute = (allowedRoutes, route) => allowedRoutes.includes(ALL_ACCESS) || allowedRoutes.includes(route);

// A CHAPEA user's nav must key off route access, not the build-time `config.mode`,
// so the CHAPEA page-links (Convert Files, IOT Download) appear for CHAPEA users in
// every environment — including PRODUCTION builds where `config.mode` is not 'CHAPEA'.
// ADMIN (all-access) is excluded: admins get the full navigation, not the CHAPEA subset.
export const isChapeaExperience = (allowedRoutes) => {
  if (!Array.isArray(allowedRoutes) || allowedRoutes.includes(ALL_ACCESS)) {
    return false;
  }
  return canAccessRoute(allowedRoutes, ROUTES.IOT_DOWNLOAD)
    || canAccessRoute(allowedRoutes, ROUTES.CONVERT_FILES);
};

// The CHAPEA page-links dropdown (Convert Files / IOT Download) is shown to genuine
// CHAPEA users everywhere, and additionally to all-access ADMIN users while they are
// on a CHAPEA page route (/convert-files or /iot-download) so admins can switch
// between the two pages via the same upper-right dropdown. ADMIN keeps its full
// navigation elsewhere because this only reports true on those routes.
export const CHAPEA_PAGE_ROUTES = [ROUTES.CONVERT_FILES, ROUTES.IOT_DOWNLOAD];

export const showChapeaPageDropdown = (allowedRoutes, pathname) => {
  if (isChapeaExperience(allowedRoutes)) {
    return true;
  }
  return canAccessRoute(allowedRoutes, ROUTES.IOT_DOWNLOAD)
    && canAccessRoute(allowedRoutes, ROUTES.CONVERT_FILES)
    && CHAPEA_PAGE_ROUTES.includes(pathname);
};

export const ENV_ROUTE_MAP = {
  ARGOS: ROUTES.ARGOS,
  'FIELD TEST': ROUTES.FIELD_TEST,
};

export const getAllowedEnvironments = (allowedRoutes, roles = []) => {
  if (allowedRoutes.includes(ALL_ACCESS)) {
    return ['NBL', 'ARGOS', 'FIELD TEST'];
  }

  // Special case for FOD: they can see NBL environment in dashboard
  const normalizedRoles = roles.map((r) => String(r || '').trim().toUpperCase());
  if (normalizedRoles.includes('FOD') && allowedRoutes.includes(ROUTES.DASHBOARD)) {
    return ['NBL'];
  }

  // For other roles, check their route access
  const environments = [];
  if (allowedRoutes.includes(ROUTES.ARGOS)) {
    environments.push('ARGOS');
  }
  if (allowedRoutes.includes(ROUTES.FIELD_TEST)) {
    environments.push('FIELD TEST');
  }

  return environments;
};

export const getDefaultRoute = (allowedRoutes) => {
  if (allowedRoutes.includes(ALL_ACCESS)) {
    return ROUTES.DASHBOARD;
  }
  if (canAccessRoute(allowedRoutes, ROUTES.CONVERT_FILES)) {
    return ROUTES.CONVERT_FILES;
  }
  return ROUTES.DASHBOARD;
};
